'use client';

import React, { useMemo } from 'react';
import { generateSlug } from '@/lib/slugify';

export default function TeacherUpcomingClasses({ upcomingCourses }) {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get earliest section start date for a course
  const getEarliestSectionStart = (course) => {
    if (!Array.isArray(course?.sections)) return null;

    const dates = course.sections
      .map((s) => s?.startDate)
      .filter(Boolean)
      .map((d) => new Date(d).getTime())
      .filter((t) => !isNaN(t));

    if (dates.length === 0) return null;

    return Math.min(...dates);
  };

  // Sort by earliest section start date (soonest → latest)
  const sortedUpcomingCourses = useMemo(() => {
    const list = Array.isArray(upcomingCourses) ? [...upcomingCourses] : [];

    return list.sort((a, b) => {
      const aStart = getEarliestSectionStart(a);
      const bStart = getEarliestSectionStart(b);

      if (aStart === null && bStart === null) return 0;
      if (aStart === null) return 1;
      if (bStart === null) return -1;

      return aStart - bStart;
    });
  }, [upcomingCourses]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900">
            Upcoming Classes
          </h2>
        </div>
      </div>

      <div className="px-6 py-4">
        {sortedUpcomingCourses.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No upcoming classes scheduled.
          </p>
        ) : (
          <div className="space-y-4">
            {sortedUpcomingCourses.map((course) => {
              const earliestStart = getEarliestSectionStart(course);
              const earliestSection = course.sections?.find(
                (s) =>
                  new Date(s.startDate).getTime() === earliestStart
              );

              return (
                <a
                  key={course.id}
                  href={`/classes/${generateSlug(course.name, course.id)}`}
                  className="block"
                  aria-label={`Open class: ${course.name}`}
                >
                  <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all">
                    <img
                      src={course.imageUrl || '/placeholder.svg'}
                      alt={course.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />

                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {course.name}
                      </h3>

                      {earliestSection && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            {formatDate(earliestSection.startDate)}
                          </div>

                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                              />
                            </svg>
                            Ages: {course.ages || 'N/A'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}